import { Seeder } from '../seeder'
import { bsConstants } from '../../module.constants'
import { LiveSeedBooks, SeedBook, SeedBooks } from './interfaces'
import { SeedCategories } from '../../seeder.constants'
import { dateMinusDays } from 'booksprout'
import { PenNameSeeder } from '../pen-name'
import { UserSeeder } from '../user'
import { dateAddDays, newDate } from '../../utils'

/** A DATE column carries a day, not an instant - and the fixtures have to match it exactly. */
const calendarDay = (value: Date): string => value.toISOString().slice(0, 10)

const userSeeder = new UserSeeder()
const seedUsers = { ...userSeeder.getLiveDataObject(), ...userSeeder.getTestDataObject() }

const penNameSeeder = new PenNameSeeder()
const seedPenNames = { ...penNameSeeder.getLiveDataObject(), ...penNameSeeder.getTestDataObject() }

export class BookSeeder extends Seeder<SeedBooks, {}, SeedBook> {
  type = 'book'
  maxModelCount = 100

  base = {
    categories: [{ id: SeedCategories.SpeculativeFiction_ScienceFiction.id }],
    wordCount: 200,
    audience: bsConstants.BOOKS.AUDIENCE_TYPE.GENERAL,
    language: bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.ENGLISH,
    penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
    epubFile: 'testFile.epub',
    pdfFile: 'testFile.pdf',
    epubFileSizeBytes: 387019,
    pdfFileSizeBytes: 1132931,
    awaitingBookFileFlag: bsConstants.BOOKS.AWAITING_BOOK_FILES.ALLOW_ALL
  }

  getGeneralTestBook (id: number, title: string, linkAmazonAsin: string) {
    return {
      ...this.base,
      id,
      title: title,
      description: title + ' - Desc',
      penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
      authorPenNameId: seedPenNames.GeneralAuthor.id,
      bookCover: 'test.png',
      releaseDate: dateMinusDays(14), // today - 14 days,
      userId: seedUsers.Me.id,
      linkAmazonAsin,
      keywords: 'General Keyword, ' + title + ' Keyword'
    }
  }

  complexOverdueBook () {
    return {
      ...this.getGeneralTestBook(this.getNextId(), 'Complex Overdue', 'asin56'),
      linkGoodreads: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
      linkBookbub: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal',
      linkBarnes: 'https://www.barnesandnoble.com/w/adventures-of-huckleberry-finn-barnes-noble-classics-series-mark-twain/1106017531?ean=9781593081126#/',
      linkGoogle: 'https://play.google.com/books/edition/Heir_to_the_Empire/mIssn64dImoC?hl=en',
      linkKobo: '',
      linkSmashwords: 'https://www.smashwords.com/books/view/1082816',
      smashwordsCouponCode: '',
      linkITunes: 'https://books.apple.com/gb/book/heir-to-the-empire-star-wars-the-thrawn-trilogy/id438095653',
      linkAudibleUs: 'https://www.audible.com/pd/Star-Wars-Heir-to-the-Empire-20th-Anniversary-Edition-The-Thrawn-Trilogy-Book-1-Audiobook/B005IHB0OA?qid=1620646506&sr=1-1&ref=a_search_c3_lProduct_1_1&pf_rd_p=c6e316b8-14da-418d-8f91-b3cad83c5183&pf_rd_r=NREC5JDAWZ9X3QZDJJMX',
      linkAudibleUk: 'https://www.audible.co.uk/pd/Star-Wars-Heir-to-the-Empire-20th-Anniversary-Edition-The-Thrawn-Trilogy-Book-1-Audiobook/B005IHB0OA?qid=1620646506&sr=1-1&ref=a_search_c3_lProduct_1_1&pf_rd_p=c6e316b8-14da-418d-8f91-b3cad83c5183&pf_rd_r=NREC5JDAWZ9X3QZDJJMX'
    }
  }

  getLiveDataObject (): LiveSeedBooks {
    return {
      DeletedBook: {
        ...this.base,
        id: 1,
        title: '[Deleted Book]',
        description: '[Deleted Book]',
        authorPenNameId: seedPenNames.DeletedPenName.id,
        userId: seedUsers.DeletedUser.id,
        bookCover: 'awaiting-image.png',
        categories: [{ id: 1 }],
        releaseDate: newDate(0)
      }
    }
  }

  getTestDataObject (): SeedBooks {
    if (process.env.BUILD_MODE === 'master') {
      return {} as unknown as SeedBooks
    }

    const testData = {
      Allegiance: {
        ...this.base,
        id: this.getNextId(),
        audience: bsConstants.BOOKS.AUDIENCE_TYPE.MATURE,
        title: 'Allegiance',
        description: `A Star Wars novel set between the events of Epsiode IV: A New Hope and Epsiode V: The Empire Strikes Back, it features a younger Han, Leia, and Luke, as well as Mara Jade.

Mara Jade, the Emperor's Hand, is looking into the illicit financial affairs of a planetary governor, hoping to find evidence that he might be funding the Rebellion. Luke, Han, and Chewbacca are sent on a mission to help some rebel supporters. Leia is busy being a diplomat, trying to drum up support for the Rebel Alliance. And a band of 5 stormtroopers, on the run after refusing a direct order, finds itself in the strange position of doing good deeds...and perhaps even aiding the Rebellion.

The paths of all of them will crisscross back and forth as they come closer and closer to meeting up with one another...but never quite managing to.`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'Allegiance.jpg',
        releaseDate: newDate(new Date(2008, 1, 3)),
        linkAmazonAsin: '0099491974',
        userId: seedPenNames.TimothyZahn.userId,
        linkAmazon: 'amazon link',
        linkBookbub: 'bookbub',
        linkGoodreads: 'good reads',
        linkGoogle: 'google',
        keywords: 'keyword 1, keyword 2, keyword 3, keyword 4, keyword 5'
      },
      Annihilation: {
        ...this.base,
        id: this.getNextId(),
        title: 'Annihilation',
        description: `Republic agent Theron Shan and his Twi lek compadre, Teff ith must contend with a Sith Empire counter-attack against the Republic, spearheaded by the lethal apprentice of Darth Malgus. Satele Shan and Jace Malcolm co-star in what will be a fast-paced and tension-fraught tale based on the award-winning video game from BioWare and LucasArts.`,
        authorPenNameId: seedPenNames.DrewKarpyshyn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'Annihilation.jpg',
        releaseDate: newDate(new Date(2013, 5, 24)),
        linkAmazonAsin: '1781165475',
        userId: seedPenNames.DrewKarpyshyn.userId
      },
      ChoicesOfOne: {
        ...this.base,
        id: this.getNextId(),
        title: 'Choices of One Paperback',
        description: `2011 marks the 20th anniversary of the publication of Star Wars: Heir to the Empire by Timothy Zahn, the #1 New York Times hardcover bestseller that relaunched the entire Star Wars publishing program. To celebrate this historic event, Zahn returns to Star Wars with a brand-new novel of adventure, action, and intrigue starring the young Luke Skywalker, Han Solo, Princess Leia Organa, and the beloved Mara Jade!`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'ChoicesOfOne.jpg',
        releaseDate: newDate(new Date(2012, 6, 28)),
        linkAmazonAsin: '0099542633',
        linkGoodreads: 'https://www.goodreads.com',
        userId: seedPenNames.TimothyZahn.userId
      },
      DarkForceRising: {
        ...this.base,
        id: this.getNextId(),
        title: 'Dark Force Rising',
        description: `Dark Force Rising (Spectra)`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'DarkForceRising.jpg',
        releaseDate: newDate(new Date(1993, 1, 1)),
        linkAmazonAsin: 'B000S9GZQQ',
        linkITunes: 'itunes.com',
        linkBookbub: 'bookbub.com',
        userId: seedPenNames.TimothyZahn.userId,
        seriesName: 'Star Wars Thrawn trilogy',
        seriesNumber: 2
      },
      DarkSaber: {
        ...this.base,
        id: this.getNextId(),
        title: 'Darksaber: Star Wars Legends',
        description: `Luke Skywalker and Han Solo, cloaked by the Force and riding with the hostile Sand People, have returned to the dunes of the desert planet Tatooine in hopes of finding what Luke so desperately seeks: contact with Obi-Wan Kenobi.  Luke is hoping the old Jedi Knight's spirit will tell him how to help his  love, Callista, regain her lost ability to use the Force.  Tormented and haunted, Luke cannot rest until Callista is a Jedi in the fullest sense, for only then will the link between their minds and souls be restored. Yet brewing on Tatooine is news that will shake Luke and Han and  threaten everything they value.

The disturbing piece of information is that the evil Hutts, criminal warlords of the galaxy, are building a secret superweapon: a reconstruction of the original Death Star, to be named Darksaber. This planet-crushing power will be in the ruthless hands of Durga the Hutt -- a creature without conscience or mercy.

But there is worse news yet: the Empire lives. The beautiful Admiral Daala, still very much alive and more driven than ever to destroy the Jedi, has joined forces with the defeated Pellaeon, former second in command to Grand Admiral Thrawn. Together they are marshaling Imperial forces to wipe out the New Republic.

Now, as Luke, Han, Leia, Chewbacca, Artoo and Threepio regroup to face these threats, they are joined by new Jedi Knights and Callista. Together they must
fight on two fronts, outshooting and outsmarting the most formidable enemies in the galaxy. In Darksaber the Jedi are heading for the ultimate test of their power--a test in which all the temptations of the dark side beckon.  And Luke Skywalker must draw upon his innermost resources to fight for a world in which he can not only live, but dare to love.

Features a bonus section following the novel that includes a primer on the Star Wars expanded universe, and over half a dozen excerpts from some of the most popular Star Wars books of the last thirty years!`,
        penName: 'Kevin J. Anderson',
        bookCover: 'DarkSaber.jpg',
        releaseDate: newDate(new Date(2011, 6, 28)),
        linkAmazonAsin: 'B00513HJKU',
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      Deceived: {
        ...this.base,
        id: this.getNextId(),
        title: 'Deceived: Star Wars Legends',
        description: `"Our time has come. For three hundred years we prepared; we grew stronger while you rested in your cradle of power. . . . Now your Republic shall fall."

A Sith warrior to rival the most sinister of the Order's Dark Lords, Darth Malgus brought down the Jedi Temple on Coruscant in a brutal assault that shocked the galaxy. But if war crowned him the darkest of Sith heroes, peace will transform him into something far more heinous--something Malgus would never want to be but cannot stop becoming, any more than he can stop the rogue Jedi fast approaching. Her name is Aryn Leneer--and the lone Jedi Knight that Malgus cut down in the fierce battle for the Jedi Temple was her Master. Now she's going to find out what happened to him, even if it means breaking every rule in the book.`,
        penName: 'Paul S. Kemp',
        bookCover: 'Deceived.jpg',
        releaseDate: newDate(new Date(2012, 5, 29)),
        linkAmazonAsin: '0345511395',
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      HeirToTheEmpire: {
        ...this.base,
        id: this.getNextId(),
        title: 'Heir to the Empire',
        description: `For use in schools and libraries only. Five years after they defeated Darth Vader and the Emperor, Princess Leia and Han Solo are married and expecting children, and Luke is a Jedi knight, and all are unaware that the last of the Emperor's warlords plan to reclaim the empire.`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'HeirToTheEmpire.jpg',
        releaseDate: newDate(new Date(1992, 5, 1)),
        linkAmazonAsin: '0606107215',
        userId: seedPenNames.TimothyZahn.userId,
        seriesName: 'Star Wars Thrawn trilogy',
        seriesNumber: 1
      },
      IJedi: {
        ...this.base,
        id: this.getNextId(),
        title: 'I, Jedi: Star Wars',
        description: `New York Times bestselling author Michael A. Stackpole presents a stirring new tale set in the Star Wars® universe: the dramatic story of a heroic X-wing pilot on the razor's edge between the Force--and the dark side.

Corran Horn has distinguished himself as one of the best and brightest of Rogue Squadron's elite fighting force. Then his wife, Mirax, vanishes on a covert mission for the New Republic, and Corran vows to find her. To do so, he knows he must develop the latent Force powers inherited from his grandfather, a legendary Jedi hero. He joins Luke Skywalker's famed Jedi academy to begin training, only to quit in frustration at Skywalker's methods. Now Corran is on his own. Using his Corellian undercover experience, he must infiltrate, sabotage, and destroy a ruthless organization in order to find his wife. But to succeed, Corran will have to come to terms with his Jedi heritage--and make a terrible choice: surrender to the dark side...or die.

Features a bonus section following the novel that includes a primer on the Star Wars expanded universe, and over half a dozen excerpts from some of the most popular Star Wars books of the last thirty years!`,
        penName: 'Michael A. Stackpole',
        bookCover: 'IJedi.jpg',
        releaseDate: newDate(new Date(2011, 6, 28)),
        linkAmazonAsin: 'B00513D3QE',
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      LostTribeOfTheSith: {
        ...this.base,
        id: this.getNextId(),
        title: 'Star Wars Lost Tribe of the Sith',
        description: `This collection of nine Lost Tribe of the Sith stories is for fans of the New
York Times bestselling Fate of the Jedi series, as it features the origin story
of the tribe of Sith that play such a crucial role in those novels.
The bestselling Fate of the Jedi novels introduce the Star Wars universe to a
long-forgotten tribe of Sith. This collection of stories tells the origin of that tribe.
Written by John Jackson Miller, this paperback will include all eight of the Lost
Tribe of the Sith stories, as well as "Pandemonium," the never-before-seen final
chapter that will only be available in this collection.`,
        penName: 'John Jackson Miller',
        bookCover: 'LostTribeOfTheSith.jpg',
        releaseDate: newDate(new Date(2012, 8, 2)),
        linkAmazonAsin: '0099542943',
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      OutboundFlight: {
        ...this.base,
        id: this.getNextId(),
        title: 'Star Wars: Outbound Flight',
        description: `Before the start of the Clone Wars, the future Emperor was already maneuvering to destroy the Jedi. This is the tale of the doomed Outbound Flight Project, which began as a new hope for the galaxy...and ended in despair. A grand Jedi adventure by one of the most popular Star Wars authors.

Before the Clone Wars began, a group of Jedi led by Master Jorus C'baoth lobbied the Republic Senate to fund a project to search for and contact intelligent life outside the known galaxy. Six Jedi Masters, 12 Jedi Knights, and 50,000 additional support personnel boarded an incredible starship and left on their adventure...only to disappear without a trace. This was the Jedi's first encounter with the aliens called the Chiss, and the New Republic's future archenemy, Thrawn. But until Luke Skywalker and his wife Mara headed out for the Unknown Regions in Survivor's Quest, the fate of the Outbound Flight Project remained an enigma.

This is the story of those Jedi Masters and Knights, their heroic quest, and their mysterious end.`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'OutboundFlight.jpg',
        releaseDate: newDate(new Date(2007, 2, 1)),
        linkAmazonAsin: '0099493586',
        userId: seedPenNames.TimothyZahn.userId
      },
      PathOfDestruction: {
        ...this.base,
        id: this.getNextId(),
        title: 'Path of Destruction',
        description: `When the Sith Wars were over, the Jedi thought the Sith had been destroyed. But one remained: Darth Bane, the most powerful Sith Lord ever. And now, in secret, he is setting out to build his new Sith Order, with which he intends to rule the galaxy, with his new apprentice by his side.`,
        authorPenNameId: seedPenNames.DrewKarpyshyn.id,
        bookCover: 'PathOfDestruction.jpg',
        releaseDate: newDate(new Date(2007, 6, 26)),
        linkAmazonAsin: '0345477375',
        userId: seedPenNames.DrewKarpyshyn.userId
      },
      RuleOfTwo: {
        ...this.base,
        id: this.getNextId(),
        title: 'Rule of Two',
        description: `"Two there should be; no more, no less.
One to embody the power, the other to crave it."
-Darth Bane, Dark Lord of the Sith
On the run from vengeful Republic forces, Dessel, a cortosis miner, vanishes into the ranks of the Sith army and ships out to join the bloody war against the Republic and its Jedi champions. There, Dessel's brutality, cunning, and exceptional command of the Force swiftly win him renown as a warrior. But in the eyes of his watchful masters, a far greater destiny awaits him.
As an acolyte in the Sith academy, studying the secrets and skills of the dark side, Dessel embraces his new identity: Bane. But the true test is yet to come. In order to gain acceptance into the Brotherhood of Darkness, he must defy the most sacred traditions and reject all he has been taught. It is a trial by fire in which he must surrender fully to the dark side-and forge from the ashes a new era of absolute power.

"A solid space adventure [that] charts the evolution of an antihero almost as chilling as Darth Vader."`,
        authorPenNameId: seedPenNames.DrewKarpyshyn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'RuleOfTwo.jpg',
        releaseDate: newDate(new Date(2008, 11, 6)),
        linkAmazonAsin: '0099492024',
        userId: seedPenNames.DrewKarpyshyn.userId
      },
      SecretsOfTheJedi: {
        ...this.base,
        id: this.getNextId(),
        title: 'Star Wars: The Secrets of the Jedi',
        description: `Discover the world of Star Wars' Jedi through this fun and fully interactive reading experience.
Join Jedi Master Luke Skywalker on a fully interactive journey into the world of the Jedi. Discover the storied history of this noble order of peacekeepers; explore the secrets of the Force; study the art of mastering the lightsaber; and learn about the lives of some of the most revered Jedi of all time, including Yoda, Obi-Wan Kenobi, and Anakin Skywalker. Featuring amazing original illustrations and unique special features--including a pop-up holocron, a translator card, a Jedi equipment booklet, pullouts, and more--this book is the ultimate exploration of the Jedi and their incredible impact on the galaxy.`,
        penName: 'Marc Sumerak',
        bookCover: 'SecretsOfTheJedi.jpg',
        releaseDate: newDate(new Date(2020, 6, 24)),
        linkAmazonAsin: '1683837029',
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      SpecterOfThePast: {
        ...this.base,
        id: this.getNextId(),
        title: 'Specter of the Past',
        description: `Hugo Award-winning author Timothy Zahn makes his triumphant return to the Star Wars(r) universe in this first of an epic new two-volume series in which the New Republic must face its most dangerous enemy yet--a dead Imperial warlord.

The Empire stands at the brink of total collapse. But they have saved their most heinous plan for last. First a plot is hatched that could destroy the New Republic in a bloodbath of genocide and civil war. Then comes the shocking news that Grand Admiral Thrawn--the most cunning and ruthless warlord in history--has apparently returned from the dead to lead the Empire to a long-prophesied victory. Facing incredible odds, Han and Leia begin a desperate race against time to prevent the New Republic from unraveling in the face of two inexplicable threats--one from within and one from without. Meanwhile, Luke teams up with Mara Jade, using the Force to track down a mysterious pirate ship with a crew of clones. Yet, perhaps most dangerous of all, are those who lurk in the shadows, orchestrating a dark plan that will turn the New Republic and the Empire into their playthings.

Features a bonus section following the novel that includes a primer on the Star Wars expanded universe, and over half a dozen excerpts from some of the most popular Star Wars books of the last thirty years!`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'SpecterOfThePast.jpg',
        releaseDate: newDate(new Date(2011, 6, 28)),
        linkAmazonAsin: 'B00513E5SY',
        userId: seedPenNames.TimothyZahn.userId,
        seriesName: 'The Hand of Thrawn Duology',
        seriesNumber: 1
      },
      SurvivorsQuest: {
        ...this.base,
        id: this.getNextId(),
        title: 'Star Wars: Survivor\'s Quest',
        description: `Luke Skywalker and his wife, Mara, get a strange message from the Chiss: Come and get the remains of the mysterious Outbound Flight Project. But there is more than meets the eye to this unexpected gift, as the newlyweds come face to face with the past! For someone is still alive in the Outbound Flight ships, and they're not interested in visitors. Plus there's at least one imposter along on the mission, and maybe more, and it will be up to Luke and Mara to uncover all the secrets and prevent another war from breaking out.`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'SurvivorsQuest.jpg',
        releaseDate: newDate(new Date(2005, 2, 3)),
        linkAmazonAsin: '0099472635',
        userId: seedPenNames.TimothyZahn.userId
      },
      TheLastCommand: {
        ...this.base,
        id: this.getNextId(),
        title: 'The Last Command',
        description: `Heir to The Empire was a number-one New York Times bestseller and the science-fiction publishing event of the year. Dark Force Rising continued the gripping new adventures of Luke Skywalker, Princess Leia, Han Solo, and the other heroes of the most popular trilogy in movie history. Now the saga concludes with The Last Command, the climactic volume of a three-book cycle authorized by Lucasfilm Ltd. and written by Hugo Award-winning author Timothy Zahn. It is five years after the events of Return of The Jedi. The fragile New Republic reels from the attacks of Grand Admiral Thrawn, who has not only rallied the remaining Imperial forces but has driven the rebels back with an abominable new technology: clone soldiers. Hopes are dim as Thrawn mounts a final siege against the Republic. While Han and Chewbacca struggle to form a wary alliance of smugglers in a last-ditch attack against the Empire, Leia keeps the Alliance together and prepares for the birth of her Jedi twins. But the Empire has too many ships and too many clones to combat. The Republic's only hope lies in sending a small force, led by Luke, into the very stronghold that houses Thrown's terrible cloning machines. There a final danger awaits. The Dark Jedi C'baoth schemes in his secret fortress, directing the battle against the rebels, nursing his insanity and building his strength to finish what he had already started - the destruction of Luke Skywalker. An odyssey of fast-paced action, stunning revelation, and final confrontation, The Last Command spans a galaxy in flames - a tale that will conclude in this third and last installment as Good and Evil battle "a long time ago, in a galaxy far, far away...."`,
        authorPenNameId: seedPenNames.TimothyZahn.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'TheLastCommand.jpg',
        releaseDate: newDate(new Date(1980, 1, 1)),
        linkAmazonAsin: '0553564927',
        userId: seedPenNames.TimothyZahn.userId,
        seriesName: 'Star Wars Thrawn trilogy',
        seriesNumber: 3
      },
      TruceAtBakura: {
        ...this.base,
        id: this.getNextId(),
        title: 'Union',
        description: `No sooner has Darth Vader's funeral pyre burned  to ashes on Endor than the Alliance intercepts a  call for help from a far-flung Imperial outpost.  Bakura is on the edge of known space and the first to  meet the Ssi-ruuk, cold-blooded reptilian invaders  who, once allied with the now dead Emperor, are  approaching Imperial space with only one goal; total  domination. Princess Leia sees the mission as an  opportunity to achieve a diplomatic victory for the  Alliance. But it assumes even greater importance  when a vision of Obi-Wan Kenobi appears to Luke  Skywalker with the message that he must go to  Bakura-or risk losing everything the Rebels have fought  so desperately to achieve.

Features a bonus section following the novel that includes a primer on the Star Wars expanded universe, and over half a dozen excerpts from some of the most popular Star Wars books of the last thirty years!`,
        penName: 'Kathy Tyers',
        bookCover: 'TruceAtBakura.jpg',
        releaseDate: newDate(new Date(2011, 6, 28)),
        linkAmazonAsin: 'B00513H3ME',
        userId: seedUsers.Me.id,
        seriesName: 'Star Wars Legends',
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      Union: {
        ...this.base,
        id: this.getNextId(),
        title: 'Union',
        description: `Though the New Republic is thriving, there are still many who remain true to the spirit and evil designs of Emperor Palpatine. So when Luke Skywalker, the New Republic's greatest hero and sole Jedi master, decides to marry Mara Jade, the woman who was formerly Emperor Palpatine's personal assassin and executor of the Empire's will, you can be sure that somebody is going to raise their hands in protest!`,
        penName: 'Stackpole, Teranishi, Chuckry',
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(1999, 1, 1)),
        linkAmazonAsin: 'B015ERDYQA',
        userId: seedUsers.Me.id,
        seriesName: 'Star Wars',
        seriesNumber: 1,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      FreeAuthorBook1: {
        ...this.base,
        id: this.getNextId(),
        title: 'Free Author Book 1',
        description: 'Free Author Book Description',
        seriesName: 'Series Name',
        seriesNumber: 1,
        penName: 'Free Author',
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(2020, 6, 28)),
        userId: seedUsers.FreeAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING
      },
      GrowingAuthorBook1: {
        ...this.base,
        id: this.getNextId(),
        title: 'Growing Author Book',
        description: 'Growing Author Book Description',
        seriesName: 'Series Name',
        seriesNumber: 1,
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(2020, 6, 28)),
        userId: seedUsers.GrowingAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        authorPenNameId: seedPenNames.GrowingAuthor.id
      },
      ProAuthorBook1: {
        ...this.base,
        id: this.getNextId(),
        title: 'Pro Author Book 1',
        description: 'Pro Author Book Description',
        penName: 'Pro Author',
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(2020, 6, 28)),
        linkAmazonAsin: 'B015ERDPRO',
        userId: seedUsers.ProAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        seriesName: 'Test Series',
        seriesNumber: 1
      },
      BestSellerAuthorBook1: {
        ...this.base,
        id: this.getNextId(),
        title: 'BestSeller Author Book 1',
        description: 'BestSeller Author Book Description',
        penName: 'BestSeller Author',
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(2020, 6, 28)),
        linkAmazonAsin: 'B015ERBEST',
        userId: seedUsers.BestsellingAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        seriesName: 'Test Series',
        seriesNumber: 1
      },
      BestSellerAuthorBook2: {
        ...this.base,
        id: this.getNextId(),
        title: 'BestSeller Author Book 2',
        description: 'BestSeller Author Book Description',
        penName: 'BestSeller Author',
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(2019, 6, 28)),
        linkAmazonAsin: 'B015E5BEST',
        userId: seedUsers.BestsellingAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        seriesName: 'Test Series',
        seriesNumber: 2
      },
      PublisherBook1: {
        ...this.base,
        id: this.getNextId(),
        title: 'Publisher Author Book 1',
        description: 'Publisher Author Book Description',
        penName: 'Publisher Author',
        bookCover: 'Union.jpg',
        releaseDate: newDate(new Date(2020, 6, 28)),
        userId: seedUsers.Publisher.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        seriesName: 'Test Series',
        seriesNumber: 1
      },
      ReviewDueSoon: {
        ...this.base,
        id: this.getNextId(),
        title: 'Review Due Soon Book',
        description: 'An exciting tale of a Review that\'s Due Soon',
        penName: 'Some Author',
        bookCover: 'Union.jpg',
        releaseDate: newDate(newDate().setDate(newDate().getDate() - 14)), // today - 14 days,
        userId: seedUsers.Publisher.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        linkBookbub: 'https://something.com'
      },
      FinishedArcBook: {
        ...this.base,
        id: this.getNextId(),
        title: 'This book is on a finished ARC',
        description: 'An exciting tale of how an ARC was finished!',
        penName: 'Allan',
        bookCover: 'awaiting-image.png',
        releaseDate: newDate(newDate().setDate(newDate().getDate() - 14)), // today - 14 days,
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        linkBookbub: 'https://something.com'
      },
      DraftArcBook: {
        ...this.base,
        id: this.getNextId(),
        title: 'This book is on a draft ARC',
        description: 'An exciting tale of how an ARC was left as a draft!',
        penName: 'Allan',
        bookCover: 'awaiting-image.png',
        releaseDate: newDate(newDate().setDate(newDate().getDate() - 14)), // today - 14 days,
        userId: seedUsers.Me.id,
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        linkBookbub: 'https://www.bookbub.com'
      },
      FutureArc: {
        ...this.getGeneralTestBook(this.getNextId(), 'Future ARC', 'asin1'),
        releaseDate: newDate(newDate().setDate(newDate().getDate() + 14)) // today + 14 days
      },
      NoCopiesLeft: {
        ...this.getGeneralTestBook(this.getNextId(), 'No Copies Left', 'asin2')
      },
      BlockedFromTeam: {
        ...this.getGeneralTestBook(this.getNextId(), 'Blocked from Team', 'asin3')
      },
      ReviewedBookBefore: {
        ...this.getGeneralTestBook(this.getNextId(), 'Reviewed Book Before', 'asin4')
      },
      NotReviewedBefore: {
        ...this.getGeneralTestBook(this.getNextId(), 'Not Revieed this Book Before', 'asin5')
      },
      NoCopiesButIsVip: {
        ...this.getGeneralTestBook(this.getNextId(), 'No Copies but is VIP', 'asin6')
      },
      HasCopiesLeft: {
        ...this.getGeneralTestBook(this.getNextId(), 'Has Copies Left', 'asin7')
      },
      VipEarlyAccess: {
        ...this.getGeneralTestBook(this.getNextId(), 'VipEarlyAccess', 'asin8')
      },
      AudienceGeneral: {
        ...this.getGeneralTestBook(this.getNextId(), 'General Audience', 'asin9'),
        audience: bsConstants.BOOKS.AUDIENCE_TYPE.GENERAL
      },
      AudienceTeen: {
        ...this.getGeneralTestBook(this.getNextId(), 'Tame Audience', 'asin10'),
        audience: bsConstants.BOOKS.AUDIENCE_TYPE.TAME
      },
      AudienceMature: {
        ...this.getGeneralTestBook(this.getNextId(), 'Mature Audience', 'asin11'),
        audience: bsConstants.BOOKS.AUDIENCE_TYPE.MATURE
      },
      AudienceExplicit: {
        ...this.getGeneralTestBook(this.getNextId(), 'Explicit Audience', 'asin12'),
        audience: bsConstants.BOOKS.AUDIENCE_TYPE.EXPLICIT
      },
      StoryShort: {
        ...this.getGeneralTestBook(this.getNextId(), 'Short Story', 'asin13'),
        wordCount: 200
      },
      StoryNovella: {
        ...this.getGeneralTestBook(this.getNextId(), 'Novella Story', 'asin14'),
        wordCount: 400
      },
      StoryNovel: {
        ...this.getGeneralTestBook(this.getNextId(), 'Novel Story', 'asin15'),
        wordCount: 600
      },
      BookNotOnBooksproutBefore: {
        ...this.getGeneralTestBook(this.getNextId(), 'Book not on Booksprout before!', 'asin16'),
        releaseDate: newDate(newDate().setDate(newDate().getDate() + 7))
      },
      BookHasRunBefore: {
        ...this.getGeneralTestBook(this.getNextId(), 'Book has run on Booksprout before', 'asin17')
      },
      FollowingAuthor: {
        ...this.getGeneralTestBook(this.getNextId(), 'Stan is following this author', 'asin18'),
        authorPenNameId: seedPenNames.FollowingAuthor.id
      },
      PartOfArcTeam: {
        ...this.getGeneralTestBook(this.getNextId(), 'Stan is part of this arc team', 'asin19')
      },
      HadPreviousPublic: {
        ...this.getGeneralTestBook(this.getNextId(), 'Has had a previous public arc', 'asin20')
      },
      WithPastReleaseDate: {
        ...this.getGeneralTestBook(this.getNextId(), 'Has a past release date', 'asin21'),
        releaseDate: newDate(newDate().setDate(newDate().getDate() - 365)) // today + 14 days
      },
      ExclusiveToTopReviewers: {
        ...this.getGeneralTestBook(this.getNextId(), 'Is Exclusive to top reviewers', 'asin22')
      },
      VipEarlyAccessEnded: {
        ...this.getGeneralTestBook(this.getNextId(), 'Vip Early access has ended!', 'asin22')
      },
      ComplicatedSites: {
        ...this.getGeneralTestBook(this.getNextId(), 'Complicated Sites!', 'asin234567'),
        smashwordsCouponCode: 'SmashingCouponCode123',
        linkSmashwords: 'https://www.smashwords.com/books/view/572466'
      },
      Complete: {
        ...this.getGeneralTestBook(this.getNextId(), 'Complete', 'asin24'),
        releaseDate: dateMinusDays(60)
      },
      Cancelled: {
        ...this.getGeneralTestBook(this.getNextId(), 'Cancelled', 'asin25')
      },
      PendingPublishing: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing', 'asin26'),
        linkGoogle: 'https://www.google.com'
      },
      PendingPublishingWithOptionalStores: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing with optional stores', 'asin27')
      },
      Active: {
        ...this.getGeneralTestBook(this.getNextId(), 'Active', 'asin28')
      },
      Due: {
        ...this.getGeneralTestBook(this.getNextId(), 'Due', 'asin29'),
        linkGoogle: 'https://www.google.com',
        linkGoodreads: 'https://www.goodreads.com',
        linkITunes: 'https://www.itunes.com'
      },
      DueOneDelayedOtherAvailable: {
        ...this.getGeneralTestBook(this.getNextId(), 'Due one delayed other available', 'asin30'),
        linkGoogle: 'https://www.google.com'
      },
      Delayed: {
        ...this.getGeneralTestBook(this.getNextId(), 'Delayed', 'asin31'),
        linkSmashwords: 'https://www.smashwords.com',
        linkGoogle: 'https://www.google.com'
      },
      OneDelayedOneWaitingLinkOneReady: {
        ...this.getGeneralTestBook(this.getNextId(), 'One delayed - one waiting link - one ready', 'asin32'),
        linkGoogle: 'https://www.google.com',
        linkSmashwords: 'https://www.smashwords.com'
      },
      Private: {
        ...this.getGeneralTestBook(this.getNextId(), 'Private ARC', 'asin33')
      },
      DueOnGeneralTeam: {
        ...this.getGeneralTestBook(this.getNextId(), 'Due on general team', 'asin34')
      },
      AllSitesWithLinks: {
        ...this.getGeneralTestBook(this.getNextId(), 'All sites with links', '1787466329'),
        linkGoodreads: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBookbub: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal',
        linkBarnes: 'https://www.barnesandnoble.com/w/adventures-of-huckleberry-finn-barnes-noble-classics-series-mark-twain/1106017531?ean=9781593081126#/',
        linkGoogle: 'https://play.google.com/books/edition/Heir_to_the_Empire/mIssn64dImoC?hl=en',
        linkKobo: 'https://www.kobo.com/th/en/ebook/the-adventures-of-huckleberry-finn-8',
        linkSmashwords: 'https://www.smashwords.com/books/view/1082816',
        smashwordsCouponCode: '',
        linkITunes: 'https://books.apple.com/gb/book/heir-to-the-empire-star-wars-the-thrawn-trilogy/id438095653',
        linkAudibleUs: 'https://www.audible.com/pd/Star-Wars-Heir-to-the-Empire-20th-Anniversary-Edition-The-Thrawn-Trilogy-Book-1-Audiobook/B005IHB0OA?qid=1620646506&sr=1-1&ref=a_search_c3_lProduct_1_1&pf_rd_p=c6e316b8-14da-418d-8f91-b3cad83c5183&pf_rd_r=NREC5JDAWZ9X3QZDJJMX',
        linkAudibleUk: 'https://www.audible.co.uk/pd/Star-Wars-Heir-to-the-Empire-20th-Anniversary-Edition-The-Thrawn-Trilogy-Book-1-Audiobook/B005IHB0OA?qid=1620646506&sr=1-1&ref=a_search_c3_lProduct_1_1&pf_rd_p=c6e316b8-14da-418d-8f91-b3cad83c5183&pf_rd_r=NREC5JDAWZ9X3QZDJJMX'
      },
      SeriesBook1: {
        ...this.getGeneralTestBook(this.getNextId(), 'Series book 1', 'sb1'),
        seriesName: 'Series book',
        seriesNumber: 1
      },
      SeriesBook2: {
        ...this.getGeneralTestBook(this.getNextId(), 'Series book 2', 'sb2'),
        seriesName: 'Series book',
        seriesNumber: 2
      },
      BlockedFromTeamAndReported: {
        ...this.getGeneralTestBook(this.getNextId(), 'Blocked from Team and reported', 'asin3')
      },
      TooManyCancellationsReported: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too many cancellations', 'asin44')
      },
      NotificationAuthor: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications', 'asin45'),
        linkBookbub: 'https://www.bookbub.com',
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationAuthorOtherLanguage: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Other language', 'asin54'),
        language: bsConstants.BOOKS.LANGUAGE_FLAGS.FLAGS.GERMAN,
        linkBookbub: 'https://www.bookbub.com',
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationAuthorTimeToPublish: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Time to publish', 'asin46'),
        linkBookbub: 'https://www.bookbub.com',
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationDelayExpiring: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Delay Expiring', 'asin47'),
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationDelayExpired: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Delay Expired', 'asin48'),
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationReadyToReviewEverywhere: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Ready to review', 'asin49'),
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationDueSoon: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Due soon', 'asin50'),
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      NotificationDueSoonShouldNotGenerate: {
        ...this.getGeneralTestBook(this.getNextId(), 'Test notifications - Due soon (should not generate)', 'asin50'),
        authorPenNameId: seedPenNames.NotificationAuthor.id,
        userId: seedUsers.NotificationAuthor.id
      },
      HasReportedPirate: {
        ...this.getGeneralTestBook(this.getNextId(), 'Has reported pirate', 'asin51')
      },
      HasReviews: {
        ...this.getGeneralTestBook(this.getNextId(), 'Has reviews', 'asin52')
      },
      OptionalStores: {
        ...this.getGeneralTestBook(this.getNextId(), 'Optional stores', 'asin53')
      },
      Scheduled: {
        ...this.getGeneralTestBook(this.getNextId(), 'Scheduled', 'asin55')
      },
      ComplexOverdue: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueAmazonDelayedOneComplete: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueBook5Days: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueBook4Days: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueBookToday: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueBookYesterday: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueBookInFuture: {
        ...this.complexOverdueBook()
      },
      ComplexOverdueBook4DaysAwaitingPublish: {
        ...this.complexOverdueBook()
      },
      AllSitesNoLinks: {
        ...this.getGeneralTestBook(this.getNextId(), 'All sites no links', '1787466329')
      },
      OutstandingReviewWithClaim: {
        ...this.getGeneralTestBook(this.getNextId(), 'Outstanding review with claimed book', '1787466500')
      },
      OutstandingReviewToBeClaimed: {
        ...this.getGeneralTestBook(this.getNextId(), 'Outstanding review to be claimed book', '1787466501')
      },
      AuthorsDirectAndKoboAudiobook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Authors Direct and Kobo audiobook', ''),
        linkITunesAudio: 'https://books.apple.com/gb/book/heir-to-the-empire-star-wars-the-thrawn-trilogy/id438095653',
        linkGoogleAudio: 'https://play.google.com/books/edition/Heir_to_the_Empire/mIssn64dImoC?hl=en',
        linkBarnesAudio: 'https://www.barnesandnoble.com/w/adventures-of-huckleberry-finn-barnes-noble-classics-series-mark-twain/1106017531?ean=9781593081126#/',
        linkKoboAudio: 'https://www.kobo.com/th/en/ebook/the-adventures-of-huckleberry-finn-8',
        linkGoodreadsAudio: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBookbubAudio: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal'
      },
      AuthorsDirectAndKoboAudiobookForNewArc: {
        ...this.getGeneralTestBook(this.getNextId(), 'Authors Direct and Kobo audiobook for new arc', ''),
        linkITunesAudio: 'https://books.apple.com/gb/book/heir-to-the-empire-star-wars-the-thrawn-trilogy/id438095653',
        linkGoogleAudio: 'https://play.google.com/books/edition/Heir_to_the_Empire/mIssn64dImoC?hl=en',
        linkBarnesAudio: 'https://www.barnesandnoble.com/w/adventures-of-huckleberry-finn-barnes-noble-classics-series-mark-twain/1106017531?ean=9781593081126#/',
        linkKoboAudio: 'https://www.kobo.com/th/en/ebook/the-adventures-of-huckleberry-finn-8',
        linkGoodreadsAudio: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBookbubAudio: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal'
      },
      RequiredSiteIsDelayedWithOptionalSitesDone: {
        ...this.getGeneralTestBook(this.getNextId(), 'Required site is delayed with optional sites done', '1787466502'),
        linkBookbub: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal',
        linkGoodreads: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBarnes: 'https://www.barnesandnoble.com/w/adventures-of-huckleberry-finn-barnes-noble-classics-series-mark-twain/1106017531?ean=9781593081126#/',
        linkITunes: 'https://books.apple.com/gb/book/heir-to-the-empire-star-wars-the-thrawn-trilogy/id438095653',
      },
      BookForRandomArcs: {
        ...this.getGeneralTestBook(this.getNextId(), 'Book for random arcs', '1787446500'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id
      },
      BookToRunAudioAndEBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Book for ebook and audio book', '1887446381'),
        linkGoodreads: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBookbub: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal',
        linkBarnes: 'https://www.barnesandnoble.com/w/adventures-of-huckleberry-finn-barnes-noble-classics-series-mark-twain/1106017531?ean=9781593081126#/',
        linkGoogle: 'https://play.google.com/books/edition/Heir_to_the_Empire/mIssn64dImoC?hl=en',
        linkKobo: 'https://www.kobo.com/th/en/ebook/the-adventures-of-huckleberry-finn-8',
        linkSmashwords: 'https://www.smashwords.com/books/view/1082816',
        smashwordsCouponCode: '',
        linkITunes: 'https://books.apple.com/gb/book/heir-to-the-empire-star-wars-the-thrawn-trilogy/id438095653',
        linkAudibleUs: 'https://www.audible.com/pd/Star-Wars-Heir-to-the-Empire-20th-Anniversary-Edition-The-Thrawn-Trilogy-Book-1-Audiobook/B005IHB0OA?qid=1620646506&sr=1-1&ref=a_search_c3_lProduct_1_1&pf_rd_p=c6e316b8-14da-418d-8f91-b3cad83c5183&pf_rd_r=NREC5JDAWZ9X3QZDJJMX',
        linkAudibleUk: 'https://www.audible.co.uk/pd/Star-Wars-Heir-to-the-Empire-20th-Anniversary-Edition-The-Thrawn-Trilogy-Book-1-Audiobook/B005IHB0OA?qid=1620646506&sr=1-1&ref=a_search_c3_lProduct_1_1&pf_rd_p=c6e316b8-14da-418d-8f91-b3cad83c5183&pf_rd_r=NREC5JDAWZ9X3QZDJJMX',
        authorPenNameId: seedPenNames.EbookAndAudioBook.id,
        userId: seedUsers.EbookAndAudioBook.id
      },
      DueForTooLong: {
        ...this.getGeneralTestBook(this.getNextId(), 'Due for too long', '1787446501'),
        authorPenNameId: seedPenNames.DueForTooLong.id,
        userId: seedUsers.Me.id
      },
      BookForEndlessArc: {
        ...this.getGeneralTestBook(this.getNextId(), 'Book for endless arc', '1787446502'),
        authorPenNameId: seedPenNames.GeneralAuthor.id,
        userId: seedUsers.Me.id
      },
      PendingPublishingWithReleaseDateInFuture: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing book release in future', 'asin26'),
        linkAmazonAsin: '8340283474',
        releaseDate: dateAddDays(7)
      },
      PendingPublishingWithReleaseDateInFutureButHasPrintAsin: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing book release in future and has print ASIN', 'asin26'),
        linkAmazonAsin: '',
        linkAmazonAsinPrint: '8340283475',
        releaseDate: dateAddDays(7)
      },
      PendingPublishingWithReleaseDateInFutureWithOptionalSites: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing book release in future with optional sites', 'asin26'),
        linkAmazonAsin: '8340283474',
        linkGoodreads: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBookbub: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal',
        releaseDate: dateAddDays(7)
      },
      PendingPublishingWithReleaseDateInFutureWithRequiredSites: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing book release in future with required sites', 'asin26'),
        linkAmazonAsin: '8340283474',
        linkGoodreads: 'https://www.goodreads.com/book/show/2429135.The_Girl_with_the_Dragon_Tattoo',
        linkBookbub: 'https://www.bookbub.com/books/the-boat-of-a-million-years-by-poul-anderson-2019-04-03?ebook_deal',
        releaseDate: dateAddDays(7)
      },
      PendingPublishingWithReleaseDateInPastAndHasPrintAsin: {
        ...this.getGeneralTestBook(this.getNextId(), 'Pending publishing book release in past and has print ASIN', 'asin26'),
        linkAmazonAsin: '',
        linkAmazonAsinPrint: '8340283475',
        releaseDate: dateAddDays(-7)
      },
      ArcForSameBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Arc for same book book', 'asin27'),
        userId: seedUsers.ArcForSameBook.id,
        // With userId, because getGeneralTestBook pairs Me with Me's pen name. Overriding one and
        // not the other left this book MY_BOOK against another account's pen name. [sc-27271]
        authorPenNameId: seedPenNames.ArcForSameBook.id,
        releaseDate: dateAddDays(-7)
      },
      ArcTeamRestrictions: {
        ...this.getGeneralTestBook(this.getNextId(), 'Arc team restrictions book', 'asin28'),
        userId: seedUsers.ArcTeamRestrictions.id,
        authorPenNameId: seedPenNames.ArcTeamRestrictions.id,
        releaseDate: dateAddDays(0)
      },
      TooManyWithdrawals1: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too Many Withdrawals 1', 'tmw1'),
        // These name their author in free text, which is the PUBLISHING shape - the MY_BOOK
        // pen-name row came from getGeneralTestBook and pointed at Me's, not this author's.
        // [sc-27271]
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        authorPenNameId: void 0,
        penName: 'TooManyWithdrawals1 Pen Name',
        userId: seedUsers.TooManyWithdrawalsAuthor.id
      },
      TooManyWithdrawals2: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too Many Withdrawals 2', 'tmw2'),
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        authorPenNameId: void 0,
        penName: 'TooManyWithdrawals1 Pen Name',
        userId: seedUsers.TooManyWithdrawalsAuthor.id
      },
      TooManyWithdrawals3: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too Many Withdrawals 3', 'tmw3'),
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        authorPenNameId: void 0,
        penName: 'TooManyWithdrawals1 Pen Name',
        userId: seedUsers.TooManyWithdrawalsAuthor.id
      },
      TooManyWithdrawals4: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too Many Withdrawals 4', 'tmw4'),
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        authorPenNameId: void 0,
        penName: 'TooManyWithdrawals1 Pen Name',
        userId: seedUsers.TooManyWithdrawalsAuthor.id
      },
      TooManyWithdrawals5: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too Many Withdrawals 5', 'tmw5'),
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        authorPenNameId: void 0,
        penName: 'TooManyWithdrawals1 Pen Name',
        userId: seedUsers.TooManyWithdrawalsAuthor.id
      },
      TooManyWithdrawalsReviewOutstanding: {
        ...this.getGeneralTestBook(this.getNextId(), 'Too Many Withdrawals - Reivew Outstanding', 'tmw6'),
        penNameType: bsConstants.PEN_NAME.TYPE.PUBLISHING,
        authorPenNameId: void 0,
        penName: 'TooManyWithdrawals Pen Name',
        userId: seedUsers.TooManyWithdrawalsAuthor.id
      },
      AmazonDelayedRequiredBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Amazon delayed required book', 'adirb1')
      },
      AmazonDelayedMultiBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Amazon delayed multi book', 'adimb1')
      },
      AmazonRejectedRequiredBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Amazon rejected required book', 'arrb1')
      },
      AmazonRejectedMultiBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Amazon rejected multi book', 'armb1')
      },
      NoCategoriesBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'No categories book', 'nct1'),
        categories: []
      },
      LegacyBookFieldsBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Legacy book fields book', 'lbf1'),
        createdDate: dateMinusDays(14),
        wordCount: bsConstants.BOOKS.WORD_COUNT.SHORT,
        language: void 0
      },
      AppleCodesAllClaimedBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Apple codes all claimed book', 'acacb1'),
        linkITunes: 'https://books.apple.com/gb/book/test/id123',
        // The three claimed codes have no expiry, standing in for everything added before
        // sc-28048. appleExpired1 is unclaimed, so this book only reads as "no codes available"
        // while expired codes are being kept out of the pool.
        appleBooksCodes: [
          { code: 'appleNoAvail1' },
          { code: 'appleNoAvail2' },
          { code: 'appleNoAvail3' },
          { code: 'appleExpired1', expirationDate: calendarDay(dateMinusDays(1)) }
        ]
      },
      AppleCodesAvailableBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Apple codes available book', 'acavb1'),
        linkITunes: 'https://books.apple.com/gb/book/test/id456',
        appleBooksCodes: [
          { code: 'appleAvail1', expirationDate: calendarDay(dateAddDays(90)) },
          { code: 'appleAvail2', expirationDate: calendarDay(dateAddDays(90)) },
          { code: 'appleAvail3', expirationDate: calendarDay(dateAddDays(90)) }
        ]
      },
      ReviewTranslationBook: {
        ...this.base,
        id: this.getNextId(),
        title: 'Review Translation Test Book',
        description: 'A test book for exercising translated ARC reviews.',
        authorPenNameId: seedPenNames.ReviewTranslationPenName.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'test.png',
        releaseDate: dateMinusDays(7),
        linkAmazonAsin: 'reviewtranslation001',
        userId: seedUsers.ReviewTranslationAuthor.id,
        keywords: 'translation, review, test'
      },
      // sc-26587: books for the two saved review copies email scenarios. Book 2 is part of a
      // series so the series block renders in its email too.
      SavedArcEndingSoonBook: {
        ...this.base,
        id: this.getNextId(),
        title: 'The Ending-Soon Chronicle',
        description: 'A saved review copy whose campaign is ending soon.',
        authorPenNameId: seedPenNames.SavedArcAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'test.png',
        releaseDate: dateMinusDays(7),
        linkAmazonAsin: 'savedarcendingsoon001',
        userId: seedUsers.SavedArcAuthor.id,
        keywords: 'saved, ending soon, test'
      },
      SavedArcLowCopiesBook: {
        ...this.base,
        id: this.getNextId(),
        title: 'The Low-Copies Saga',
        description: 'A saved review copy that is running low on copies.',
        authorPenNameId: seedPenNames.SavedArcAuthor.id,
        penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
        bookCover: 'test.png',
        releaseDate: dateMinusDays(7),
        linkAmazonAsin: 'savedarclowcopies001',
        userId: seedUsers.SavedArcAuthor.id,
        seriesName: 'The Low-Copies Saga',
        seriesNumber: 2,
        keywords: 'saved, low copies, series, test'
      },
      // sc-29038: one book per capped-publisher campaign - one more than the plan's initial live limit.
      CappedPublisherBooks: Array.from(
        { length: bsConstants.SUBSCRIPTIONS.PLANS.PUBLISHER.INITIAL_MAX_RUNNING_ARCS + 1 },
        (_unused, index) => ({
          ...this.base,
          id: this.getNextId(),
          title: `Capped Publisher Book ${index + 1}`,
          description: 'A book for the capped publisher scenario.',
          authorPenNameId: seedPenNames.CappedPublisher.id,
          penNameType: bsConstants.PEN_NAME.TYPE.MY_BOOK,
          bookCover: 'test.png',
          releaseDate: dateMinusDays(7),
          linkAmazonAsin: `cappedpublisher${String(index + 1).padStart(3, '0')}`,
          userId: seedUsers.CappedPublisher.id,
          keywords: 'capped, publisher, test'
        })
      ),
      // sc-29117: books for the DelayedAmazon* ARCs, on the random-ARCs author so no author list counts
      // them. The first has no Goodreads link although its reader has posted there, as on the book in the
      // support case behind the story. Titles stay within the 51 characters a review copy card shows whole
      // (BookPreview's ellipsis at 54), so the e2e can find each card by its title.
      DelayedAmazonLastReviewBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Delayed Amazon last review', 'dalastreview001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkBookbub: 'https://www.bookbub.com/books/delayed-amazon-last-review'
      },
      DelayedAmazonTwoOwedBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Delayed Amazon two reviews owed', 'datwoowed001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkGoodreads: 'https://www.goodreads.com/book/show/delayed-amazon-two-reviews-owed',
        linkBookbub: 'https://www.bookbub.com/books/delayed-amazon-two-reviews-owed'
      },
      DelayedAmazonExemptBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Delayed Amazon after an exemption', 'daexempt001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkGoodreads: 'https://www.goodreads.com/book/show/delayed-amazon-after-an-exemption',
        linkBookbub: 'https://www.bookbub.com/books/delayed-amazon-after-an-exemption'
      },
      DelayedAmazonRequiredBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Delayed Amazon required, optional owed', 'darequired001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkGoodreads: 'https://www.goodreads.com/book/show/delayed-amazon-required',
        linkBookbub: 'https://www.bookbub.com/books/delayed-amazon-required'
      },
      DelayedAmazonOptionalBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Delayed Amazon optional, required owed', 'daoptional001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkGoodreads: 'https://www.goodreads.com/book/show/delayed-amazon-optional',
        linkBookbub: 'https://www.bookbub.com/books/delayed-amazon-optional'
      },
      DelayedAmazonRejectedBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Rejected Amazon required, optional owed', 'darejected001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkGoodreads: 'https://www.goodreads.com/book/show/rejected-amazon-required',
        linkBookbub: 'https://www.bookbub.com/books/rejected-amazon-required'
      },
      DelayedAmazonRejectedLastReviewBook: {
        ...this.getGeneralTestBook(this.getNextId(), 'Rejected Amazon last review', 'darejectedlast001'),
        authorPenNameId: seedPenNames.PenNameForRandomArcs.id,
        userId: seedUsers.UserForRandomArcs.id,
        linkGoodreads: 'https://www.goodreads.com/book/show/rejected-amazon-last-review',
        linkBookbub: 'https://www.bookbub.com/books/rejected-amazon-last-review'
      }
    }

    return testData
  }
}
